import React from 'react';
import LegalPage from './LegalPage';

export default function TermsOfUsePage() {
  return (
    <LegalPage title="Terms of Use" lastUpdated="April 30, 2026">
      <h2>1. Acceptance of Terms</h2>
      <p>
        These Terms of Use (&ldquo;Terms&rdquo;) constitute a legally binding agreement between you (&ldquo;User,&rdquo; &ldquo;you,&rdquo; or &ldquo;your&rdquo;) and Tooling Management Group LLC (&ldquo;TMG,&rdquo; &ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) governing your access to and use of the ToolSight&trade; platform (the &ldquo;Service&rdquo;).
      </p>
      <p>
        By signing in to the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Notice (available at <a href="/legal/privacy">/legal/privacy</a>). If you do not agree to these Terms, you must not access or use the Service.
      </p>
      <p>
        If you are accessing the Service on behalf of an organization (such as your employer), you represent that you have the authority to bind that organization to these Terms, and &ldquo;you&rdquo; and &ldquo;your&rdquo; refer to that organization.
      </p>

      <h2>2. Description of the Service</h2>
      <p>
        ToolSight is a cloud-based tooling lifecycle management platform for the automotive industry. The Service enables authorized users to track tooling assets, manage disposal workflows, coordinate with suppliers, exchange data, and support field inventory operations.
      </p>
      <p>
        The Service is provided to users who have been provisioned an account by TMG. Access is granted in connection with a Master Service Agreement or other written engagement between TMG and an OEM client, or in connection with a supplier&rsquo;s participation in an OEM tooling program managed by TMG.
      </p>

      <h2>3. User Accounts and Access</h2>

      <h3>3.1 Account Provisioning</h3>
      <p>
        User accounts are created and managed by TMG. You will be assigned a role and site access level appropriate to your responsibilities. You may not share your account credentials with any other person.
      </p>

      <h3>3.2 Account Security</h3>
      <p>
        You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. You must notify TMG immediately if you become aware of any unauthorized use of your account or any other breach of security.
      </p>

      <h3>3.3 Account Suspension and Termination</h3>
      <p>
        TMG reserves the right to suspend or terminate your access to the Service at any time, with or without notice, for any reason, including but not limited to violation of these Terms, expiration or termination of the applicable client engagement, or at the direction of the OEM client on whose behalf your account was provisioned.
      </p>

      <h2>4. Acceptable Use</h2>
      <p>
        You agree to use the Service only for its intended purpose and in compliance with all applicable laws and regulations. You may not:
      </p>
      <ul>
        <li>Access or attempt to access data, tools, sites, or features that are not authorized for your role or site assignment</li>
        <li>Share, distribute, or disclose any data obtained through the Service to unauthorized parties</li>
        <li>Attempt to reverse engineer, decompile, disassemble, or otherwise derive the source code, algorithms, or underlying technology of the Service</li>
        <li>Use the Service to transmit malware, viruses, or any other harmful code</li>
        <li>Interfere with, disrupt, or attempt to gain unauthorized access to the Service&rsquo;s infrastructure, servers, or networks</li>
        <li>Use automated scripts, bots, or scrapers to access or extract data from the Service without TMG&rsquo;s prior written consent</li>
        <li>Use the Service for any purpose that is unlawful, fraudulent, or harmful</li>
        <li>Remove, alter, or obscure any copyright, trademark, or other proprietary notices contained in the Service</li>
        <li>Capture, reproduce, screenshot, record, or otherwise copy the visual design, screen layouts, user interface elements, navigation flows, or user experience patterns of the Service, whether manually or through automated means, for the purpose of replicating, imitating, or informing the design of any other product or service</li>
        <li>Systematically catalog, document, or inventory the features, functionality, workflows, business logic, or operational methodologies embodied in the Service</li>
        <li>Use access to the Service to build, design, develop, train, or inform any product, service, or technology that competes with or is substantially similar to the Service or any of its features</li>
        <li>Use the Service for competitive analysis, benchmarking, or evaluation purposes without TMG&rsquo;s prior written consent</li>
        <li>Grant, permit, or facilitate access to the Service by any third party that has not been authorized by TMG, including but not limited to technology vendors, integration partners, consultants, or contractors, without TMG&rsquo;s prior written consent</li>
        <li>Frame, mirror, or create derivative works based on the Service or any portion thereof</li>
      </ul>

      <h2>5. Intellectual Property</h2>

      <h3>5.1 TMG Platform Ownership</h3>
      <p>
        The Service, including all software, algorithms, data models, user interfaces, documentation, proprietary methodologies, and trade secrets embodied therein, is and remains the exclusive property of Tooling Management Group LLC. The Service is protected by copyright, trade secret, and other intellectual property laws. Nothing in these Terms grants you any right, title, or interest in the Service itself, except for the limited right to use it as described herein.
      </p>

      <h3>5.2 Client Data Ownership</h3>
      <p>
        Tooling and operational data processed through the Service (including tool records, part numbers, lifecycle data, disposal records, and related information) is and remains the property of the applicable OEM client, as governed by the Master Service Agreement between TMG and that client. TMG processes this data solely on behalf of and under the direction of the client.
      </p>

      <h3>5.3 User-Generated Content</h3>
      <p>
        Content you create or upload through the Service &mdash; including photographs, field notes, condition assessments, and data entries &mdash; is contributed in connection with the applicable client engagement. By uploading or creating content through the Service, you grant TMG a non-exclusive, royalty-free license to use, store, process, and display that content as necessary to operate the Service and fulfill TMG&rsquo;s obligations under the applicable client agreement.
      </p>

      <h3>5.4 Feedback</h3>
      <p>
        If you provide TMG with feedback, suggestions, or ideas regarding the Service, you acknowledge that TMG may use such feedback without restriction or obligation to you. Feedback does not create any intellectual property rights in your favor.
      </p>

      <h3>5.5 Trademarks</h3>
      <p>
        ToolSight&trade; is a trademark of Tooling Management Group LLC. You may not use the ToolSight name, logo, or any TMG trademarks without TMG&rsquo;s prior written consent, except as necessary to identify the Service in the ordinary course of your authorized use.
      </p>

      <h3>5.6 Proprietary Methodology and Trade Secrets</h3>
      <p>
        You acknowledge that the Service embodies proprietary methodologies, workflows, business logic, data models, analytical approaches, and operational processes developed by TMG over more than 20 years of automotive tooling industry experience. These elements constitute trade secrets and confidential business information of TMG, regardless of whether they are individually identified or labeled as such within the Service.
      </p>
      <p>
        The structure, organization, and sequence of the Service&rsquo;s screens, workflows, and user interfaces &mdash; including but not limited to how tooling lifecycle stages are defined and tracked, how disposal workflows are sequenced, how supplier coordination is organized, how field inventory operations are structured, and how data exchange and reconciliation are managed &mdash; reflect proprietary design decisions that are protected as TMG intellectual property.
      </p>

      <h3>5.7 Non-Compete and Non-Circumvention</h3>
      <p>
        During the term of your access to the Service and for a period of two (2) years following termination of access, you agree not to directly or indirectly use any knowledge, information, or insight gained through your use of the Service to design, develop, build, market, sell, or assist in the creation of any product, service, or platform that replicates, imitates, or is substantially similar to the Service or any of its material features or workflows.
      </p>
      <p>
        This restriction applies to you individually and to any organization on whose behalf you accessed the Service. It does not restrict your use of general industry knowledge or skills that are independent of the Service.
      </p>

      <h3>5.8 Injunctive Relief</h3>
      <p>
        You acknowledge that any breach of Sections 4, 5, or 6 of these Terms may cause irreparable harm to TMG for which monetary damages would be an inadequate remedy. Accordingly, TMG shall be entitled to seek injunctive or other equitable relief in addition to any other remedies available at law or in equity, without the necessity of proving actual damages or posting a bond.
      </p>

      <h2>6. Confidentiality</h2>
      <p>
        You acknowledge that data accessed through the Service may include confidential and proprietary information belonging to OEM clients, suppliers, and TMG. You agree to:
      </p>
      <ul>
        <li>Treat all data accessed through the Service as confidential unless explicitly designated otherwise</li>
        <li>Not disclose, share, or distribute any data obtained through the Service to any person or entity not authorized to receive it</li>
        <li>Use data accessed through the Service solely for its intended purpose within your authorized role</li>
        <li>Comply with any additional confidentiality obligations imposed by your employer, the applicable OEM client, or any separate confidentiality agreement</li>
      </ul>
      <p>Confidentiality obligations survive termination of your access to the Service.</p>

      <h2>7. Data Privacy</h2>
      <p>
        Our collection, use, and protection of information is described in our Privacy Notice, available at <a href="/legal/privacy">/legal/privacy</a>. The Privacy Notice is incorporated into these Terms by reference.
      </p>

      <h2>8. Service Availability and Modifications</h2>

      <h3>8.1 Availability</h3>
      <p>
        TMG uses commercially reasonable efforts to maintain the availability of the Service. However, the Service may be temporarily unavailable due to scheduled maintenance, updates, or circumstances beyond TMG&rsquo;s reasonable control. TMG does not guarantee uninterrupted or error-free operation of the Service.
      </p>

      <h3>8.2 Modifications</h3>
      <p>
        TMG reserves the right to modify, update, or discontinue any feature or functionality of the Service at any time. Where practicable, TMG will provide notice of material changes that significantly affect the user experience. Continued use of the Service after modifications constitutes acceptance of those changes.
      </p>

      <h3>8.3 Offline Functionality</h3>
      <p>
        Certain features of the Service may be available in an offline mode. Data entered or modified offline is synchronized with the Service when connectivity is restored. TMG is not responsible for data loss resulting from extended offline use, device failure, or failure to synchronize.
      </p>

      <h2>9. Disclaimers</h2>

      <h3>9.1 As-Is Provision</h3>
      <p className="legal-uppercase">
        The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo; without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, title, and non-infringement.
      </p>

      <h3>9.2 No Guarantee of Accuracy</h3>
      <p>
        While TMG takes reasonable steps to ensure the accuracy of data processed through the Service, including human expert review of AI-assisted outputs, TMG does not warrant that all information in the Service is complete, accurate, or current. Users and clients are responsible for independently verifying critical data before making tooling disposition or production decisions.
      </p>

      <h3>9.3 Not Professional Advice</h3>
      <p>
        The Service provides tooling lifecycle management tools and data. It does not constitute legal, financial, engineering, or other professional advice. Users should consult qualified professionals for decisions that require specialized expertise beyond the scope of the Service.
      </p>

      <h2>10. Limitation of Liability</h2>
      <p>To the maximum extent permitted by applicable law:</p>
      <p className="legal-uppercase">
        <strong>10.1</strong> In no event shall TMG, its officers, directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, loss of data, loss of production, business interruption, or cost of substitute services, arising out of or in connection with your use of or inability to use the Service, whether based on warranty, contract, tort (including negligence), strict liability, or any other legal theory.
      </p>
      <p className="legal-uppercase">
        <strong>10.2</strong> TMG&rsquo;s total aggregate liability arising out of or relating to these Terms or the Service shall not exceed the amounts paid by the applicable OEM client to TMG under the Master Service Agreement during the twelve (12) months preceding the event giving rise to the claim.
      </p>
      <p className="legal-uppercase">
        <strong>10.3</strong> The limitations in this section apply to the fullest extent permitted by law and shall survive termination of these Terms.
      </p>

      <h2>11. Indemnification</h2>
      <p>
        You agree to indemnify, defend, and hold harmless TMG and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorney&rsquo;s fees) arising out of or in connection with:
      </p>
      <ul>
        <li>Your violation of these Terms</li>
        <li>Your unauthorized use of the Service or data accessed through it</li>
        <li>Your violation of any applicable law or regulation</li>
        <li>Any claim by a third party arising from your use of the Service</li>
      </ul>

      <h2>12. Governing Law and Dispute Resolution</h2>

      <h3>12.1 Governing Law</h3>
      <p>
        These Terms shall be governed by and construed in accordance with the laws of the State of Michigan, without regard to its conflict of law principles.
      </p>

      <h3>12.2 Venue</h3>
      <p>
        Any dispute arising out of or relating to these Terms or the Service shall be brought exclusively in the state or federal courts located in Washtenaw County, Michigan, and you consent to the personal jurisdiction of such courts.
      </p>

      <h3>12.3 Informal Resolution</h3>
      <p>
        Before initiating any formal legal proceeding, the parties agree to attempt to resolve any dispute informally by contacting the other party and negotiating in good faith for a period of at least thirty (30) days.
      </p>

      <h2>13. General Provisions</h2>

      <h3>13.1 Entire Agreement</h3>
      <p>
        These Terms, together with the Privacy Notice and any applicable Master Service Agreement, constitute the entire agreement between you and TMG regarding the Service. In the event of a conflict between these Terms and a Master Service Agreement, the Master Service Agreement shall control.
      </p>

      <h3>13.2 Severability</h3>
      <p>
        If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force and effect.
      </p>

      <h3>13.3 Waiver</h3>
      <p>
        TMG&rsquo;s failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. Any waiver of any provision of these Terms shall be effective only if in writing and signed by TMG.
      </p>

      <h3>13.4 Assignment</h3>
      <p>
        You may not assign or transfer your rights or obligations under these Terms without TMG&rsquo;s prior written consent. TMG may assign these Terms in connection with a merger, acquisition, reorganization, or sale of all or substantially all of its assets.
      </p>

      <h3>13.5 Notices</h3>
      <p>
        Notices to TMG should be sent using the contact information in Section 15 below. Notices to you may be sent to the email address associated with your account.
      </p>

      <h3>13.6 Survival</h3>
      <p>
        Sections 5 (Intellectual Property, including Sections 5.6 through 5.8), 6 (Confidentiality), 9 (Disclaimers), 10 (Limitation of Liability), 11 (Indemnification), 12 (Governing Law and Dispute Resolution), and this Section 13 shall survive any termination or expiration of these Terms.
      </p>

      <h2>14. Changes to These Terms</h2>
      <p>
        TMG may update these Terms from time to time. Material changes will be communicated by email to affected users at least 14 days before they take effect. The updated Terms will display a new &ldquo;Last Updated&rdquo; date. Minor clarifications may be posted without individual notification. Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.
      </p>

      <h2>15. Contact Information</h2>
      <p>If you have questions about these Terms, please contact:</p>
      <p>
        <strong>Contact</strong><br />
        Tooling Management Group LLC<br />
        Attn: Chris Ongena, CEO<br />
        745 State Circle<br />
        Ann Arbor, MI 48108, USA<br />
        Email: <a href="mailto:chris@toolingmanagementgroup.com">chris@toolingmanagementgroup.com</a>
      </p>
      <p>
        <em>If you are an active user of the Service, your TMG account representative is also available for day-to-day questions.</em>
      </p>
    </LegalPage>
  );
}
